// =============================================================================
// HardwareIDAllocator UI — useT (typed i18next wrapper)
// =============================================================================
// react-i18next 15 + i18next 24 default the t() return type to `unknown` when
// the cross-namespace key signature can't be reduced to a leaf string. That
// kills every `{t("…")}` site in JSX (TS2322: unknown is not assignable to
// ReactNode).
//
// We never call with returnObjects:true, never pass an explicit defaultValue
// that resolves to null, and never call t(<key>, <count>) without options —
// at runtime the return is always a string. This shim simply pins the
// declared return type to that runtime reality.
//
// Drop-in replacement: change `import { useTranslation } from "@/i18n/use-t"`
// to `import { useTranslation } from "@/i18n/use-t"` and every `t("…")` call
// site automatically infers as `string`.
// =============================================================================

import { useTranslation as useTranslationRaw, type UseTranslationOptions } from "react-i18next";

export type TypedT = (
  key: string | string[],
  options?: Record<string, unknown> | string,
) => string;

export function useTranslation(
  ns?: string | string[],
  options?: UseTranslationOptions<undefined>,
): { t: TypedT; i18n: ReturnType<typeof useTranslationRaw>["i18n"]; ready: boolean } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = useTranslationRaw(ns as any, options);
  return {
    t: raw.t as unknown as TypedT,
    i18n: raw.i18n,
    ready: raw.ready,
  };
}
